import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupIcon from '@mui/icons-material/Group';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import { MAP_UI } from '../theme/mapViewUi';
import {
  buildCapDeviceTreeIdsForGroup,
  capUpperTreeIdsFromConfig,
  DEFAULT_CAP_TREE_ID,
  isCapDevice,
  isJnet2CapDevice,
  ssidBandNeedsCapTreeSelect,
  showApiWarnings,
} from '../utils/publicWifiRrmPlanUi';

function resolveCapSerial(device, allDevices) {
  if (!device) return null;
  if (isJnet2CapDevice(device) || isCapDevice(device)) {
    return device.serial_number || null;
  }
  const bySerial = new Map();
  (allDevices || []).forEach((d) => {
    const s = (d.serial_number || '').toLowerCase();
    if (s) bySerial.set(s, d);
  });
  let current = device;
  const seen = new Set();
  while (current?.parent_serial) {
    const parentKey = String(current.parent_serial).toLowerCase();
    if (seen.has(parentKey)) break;
    seen.add(parentKey);
    const parent = bySerial.get(parentKey);
    if (parent && (isCapDevice(parent) || isJnet2CapDevice(parent))) {
      return parent.serial_number;
    }
    if (!parent) {
      return current.parent_serial;
    }
    current = parent;
  }
  return null;
}

function groupHasAssignedProfiles(group) {
  return Boolean(
    (group?.ssid_ids?.length > 0) || (group?.radio_profile_ids?.length > 0),
  );
}

function flattenApplyResults(nested) {
  const flat = {};
  if (!nested || typeof nested !== 'object') return flat;
  Object.values(nested).forEach((perDevice) => {
    if (perDevice && typeof perDevice === 'object') Object.assign(flat, perDevice);
  });
  return flat;
}

function summarizeApplyResults(enqueueSnackbar, label, applyResults, totalRequested) {
  const results = applyResults || {};
  let succeeded = 0;
  let failed = 0;
  let skipped = 0;
  Object.values(results).forEach((status) => {
    if (typeof status !== 'string') return;
    if (status === 'success') succeeded += 1;
    else if (status.startsWith('failed')) failed += 1;
    else skipped += 1;
  });
  const accounted = succeeded + failed + skipped;
  if (totalRequested > 0 && accounted === 0) {
    enqueueSnackbar(
      `${label}: 0 of ${totalRequested} device(s) reached the apply phase`,
      { variant: 'info' },
    );
    return;
  }
  if (failed === 0 && skipped === 0 && succeeded > 0) {
    enqueueSnackbar(`${label} applied to ${succeeded} device(s)`, { variant: 'success' });
  } else if (failed === 0 && (succeeded > 0 || skipped > 0)) {
    enqueueSnackbar(`${label}: ${succeeded} ok${skipped ? `, ${skipped} skipped` : ''}`, { variant: 'info' });
  } else if (failed > 0) {
    enqueueSnackbar(`${label}: ${succeeded} ok, ${failed} failed${skipped ? `, ${skipped} skipped` : ''}`, { variant: 'warning' });
  }
}

const DeviceMapGroupDialog = ({
  open,
  onClose,
  device,
  allDevices = [],
  onGroupsChanged,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [groups, setGroups] = useState([]);
  const [ssidProfiles, setSsidProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addGroupId, setAddGroupId] = useState('');
  const [addGroupTreeId, setAddGroupTreeId] = useState(DEFAULT_CAP_TREE_ID);
  const [addGroupCapTreeIds, setAddGroupCapTreeIds] = useState(['5GHz_0', '5GHz_1']);
  const [addGroupConfigLoading, setAddGroupConfigLoading] = useState(false);
  const [pendingRemoveGroup, setPendingRemoveGroup] = useState(null);

  const serial = (device?.serial_number || '').toLowerCase();

  const deviceDisplayName = useMemo(() => {
    const name = (device?.name || '').trim();
    const sn = device?.serial_number || '';
    if (name && name.toLowerCase() !== sn.toLowerCase()) return name;
    return sn || 'device';
  }, [device]);

  const capSerial = useMemo(
    () => resolveCapSerial(device, allDevices),
    [device, allDevices],
  );

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setAddGroupId('');
    setAddGroupTreeId(DEFAULT_CAP_TREE_ID);
    setAddGroupCapTreeIds(['5GHz_0', '5GHz_1']);
    setPendingRemoveGroup(null);
    Promise.all([
      api.getGroups(true).catch(() => ({})),
      api.getSSIDProfiles().catch(() => ({})),
    ])
      .then(([groupRes, ssidRes]) => {
        if (cancelled) return;
        setGroups(Array.isArray(groupRes?.groups) ? groupRes.groups : []);
        setSsidProfiles(Array.isArray(ssidRes?.ssids) ? ssidRes.ssids : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const memberGroups = useMemo(() => {
    if (!serial) return [];
    return (groups || []).filter((g) =>
      (g.devices || []).some((s) => String(s).toLowerCase() === serial),
    );
  }, [groups, serial]);

  const availableGroups = useMemo(() => {
    const memberIds = new Set(memberGroups.map((g) => g.id));
    return (groups || []).filter((g) => !memberIds.has(g.id));
  }, [groups, memberGroups]);

  const selectedAddGroup = useMemo(
    () => groups.find((g) => g.id === addGroupId) || null,
    [groups, addGroupId],
  );

  const groupSsidProfiles = useMemo(() => {
    const ids = new Set((selectedAddGroup?.ssid_ids || []).map(String));
    return (ssidProfiles || []).filter((p) => ids.has(String(p.id)));
  }, [selectedAddGroup, ssidProfiles]);

  const deviceIsCap = device ? isJnet2CapDevice(device) : false;

  const inheritedCapTreeId = useMemo(() => {
    if (!selectedAddGroup?.devices?.length) return '';
    const memberSet = new Set(
      selectedAddGroup.devices.map((s) => String(s).toLowerCase()),
    );
    for (const profile of groupSsidProfiles) {
      const treeMap = profile.device_tree_ids || {};
      for (const [memberSerial, tree] of Object.entries(treeMap)) {
        if (memberSet.has(String(memberSerial).toLowerCase()) && tree) return tree;
      }
    }
    return '';
  }, [selectedAddGroup, groupSsidProfiles]);

  const addToGroupNeedsCapTree = Boolean(
    addGroupId
    && deviceIsCap
    && groupSsidProfiles.some((p) => ssidBandNeedsCapTreeSelect(p.band))
    && !inheritedCapTreeId,
  );

  useEffect(() => {
    if (!open || !addToGroupNeedsCapTree || !device?.serial_number) return undefined;
    let cancelled = false;
    setAddGroupConfigLoading(true);
    api.getDeviceConfig(device.serial_number)
      .then((resp) => {
        if (cancelled) return;
        const ids = capUpperTreeIdsFromConfig(resp?.configuration);
        const options = ids.length > 0 ? ids : ['5GHz_0', '5GHz_1'];
        setAddGroupCapTreeIds(options);
        setAddGroupTreeId(
          options.includes(DEFAULT_CAP_TREE_ID) ? DEFAULT_CAP_TREE_ID : (options[0] || DEFAULT_CAP_TREE_ID),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setAddGroupCapTreeIds(['5GHz_0', '5GHz_1']);
          setAddGroupTreeId(DEFAULT_CAP_TREE_ID);
        }
      })
      .finally(() => {
        if (!cancelled) setAddGroupConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, addToGroupNeedsCapTree, device?.serial_number]);

  const refreshGroups = useCallback(async () => {
    const groupRes = await api.getGroups(true).catch(() => ({}));
    setGroups(Array.isArray(groupRes?.groups) ? groupRes.groups : []);
    onGroupsChanged?.();
  }, [onGroupsChanged]);

  const handleClose = useCallback(() => {
    if (saving) return;
    setPendingRemoveGroup(null);
    onClose?.();
  }, [saving, onClose]);

  const handleAddToGroup = useCallback(async () => {
    if (!addGroupId || !serial) return;
    if (addToGroupNeedsCapTree && !addGroupTreeId) {
      enqueueSnackbar('Select a CAP radio slot', { variant: 'warning' });
      return;
    }
    let deviceTreeIds;
    if (addToGroupNeedsCapTree) {
      deviceTreeIds = { [serial]: addGroupTreeId };
    } else if (inheritedCapTreeId && deviceIsCap) {
      deviceTreeIds = buildCapDeviceTreeIdsForGroup(
        [device],
        [serial],
        groupSsidProfiles[0]?.band,
        inheritedCapTreeId,
      );
    }
    const body = { add_devices: [serial] };
    if (deviceTreeIds && Object.keys(deviceTreeIds).length > 0) {
      body.device_tree_ids = deviceTreeIds;
    }
    setSaving(true);
    try {
      const res = await api.updateGroup(addGroupId, body);
      showApiWarnings(enqueueSnackbar, res);
      const grp = groups.find((g) => g.id === addGroupId);
      enqueueSnackbar(`Added device to group "${grp?.name || addGroupId}"`, { variant: 'success' });
      summarizeApplyResults(enqueueSnackbar, 'SSID apply', flattenApplyResults(res?.ssid_apply_results), 1);
      summarizeApplyResults(enqueueSnackbar, 'Radio apply', flattenApplyResults(res?.radio_apply_results), 1);
      await refreshGroups();
      setAddGroupId('');
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to add device to group', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [
    addGroupId,
    serial,
    addToGroupNeedsCapTree,
    addGroupTreeId,
    inheritedCapTreeId,
    deviceIsCap,
    device,
    groupSsidProfiles,
    groups,
    enqueueSnackbar,
    refreshGroups,
  ]);

  const handleConfirmRemove = useCallback(async () => {
    if (!pendingRemoveGroup?.id || !serial) return;
    setSaving(true);
    try {
      const res = await api.updateGroup(pendingRemoveGroup.id, { remove_devices: [serial] });
      showApiWarnings(enqueueSnackbar, res);
      enqueueSnackbar(
        `Removed device from group "${pendingRemoveGroup.name || pendingRemoveGroup.id}"`,
        { variant: 'success' },
      );
      if (res?.ssid_strip_results || res?.radio_strip_results) {
        const parts = [];
        if (res.ssid_strip_results) parts.push('SSID settings updated');
        if (res.radio_strip_results) parts.push('radio profile settings updated');
        if (parts.length > 0) {
          enqueueSnackbar(parts.join(' · '), { variant: 'info' });
        }
      }
      await refreshGroups();
      setPendingRemoveGroup(null);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to remove device from group', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [pendingRemoveGroup, serial, enqueueSnackbar, refreshGroups]);

  const isManageMode = memberGroups.length > 0;
  const selectedAddGroupHasProfiles = groupHasAssignedProfiles(selectedAddGroup);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        pb: 2,
        background: MAP_UI.headerGradient,
        color: 'white',
        fontWeight: 700,
      }}
      >
        {isManageMode ? 'Manage Groups' : 'Add to Group'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: capSerial ? 0.5 : 2 }}>
          Device: <strong>{deviceDisplayName}</strong>
        </Typography>
        {capSerial && capSerial.toLowerCase() !== serial && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            CAP: <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{capSerial}</Box>
          </Typography>
        )}

        {loading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {isManageMode && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Current group membership
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                  {memberGroups.map((g) => (
                    <Chip
                      key={g.id}
                      icon={<GroupIcon sx={{ fontSize: '0.9rem !important' }} />}
                      label={g.name || g.id}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>

                {pendingRemoveGroup ? (
                  <Stack spacing={1.5}>
                    <Alert severity="warning">
                      Removing this device from group <strong>{pendingRemoveGroup.name || pendingRemoveGroup.id}</strong>
                      {' '}will strip the group&apos;s SSID and radio profile settings from the device
                      (unless it still receives them via another group or a direct assignment).
                    </Alert>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirmRemove}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        {saving ? 'Removing…' : 'Remove from group'}
                      </Button>
                      <Button onClick={() => setPendingRemoveGroup(null)} disabled={saving}>
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    {memberGroups.map((g) => (
                      <Button
                        key={g.id}
                        variant="outlined"
                        color="warning"
                        size="small"
                        onClick={() => setPendingRemoveGroup(g)}
                        sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                      >
                        Remove from &quot;{g.name || g.id}&quot;
                      </Button>
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            {isManageMode && availableGroups.length > 0 && <Divider />}

            {availableGroups.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <GroupAddIcon fontSize="small" color="action" />
                  {isManageMode ? 'Add to another group' : 'Select group'}
                </Typography>
                {selectedAddGroupHasProfiles && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This group has SSID and/or radio profile assignments. Adding this device will apply
                    those profiles automatically.
                  </Alert>
                )}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id="device-map-add-group-label">Group</InputLabel>
                  <Select
                    labelId="device-map-add-group-label"
                    label="Group"
                    value={addGroupId}
                    onChange={(e) => setAddGroupId(e.target.value)}
                  >
                    <MenuItem value=""><em>Choose a group…</em></MenuItem>
                    {availableGroups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>{g.name || g.id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {addToGroupNeedsCapTree && (
                  addGroupConfigLoading ? (
                    <Stack alignItems="center" py={1} sx={{ mb: 2 }}>
                      <CircularProgress size={22} />
                    </Stack>
                  ) : (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="device-map-cap-tree-label">CAP radio slot</InputLabel>
                      <Select
                        labelId="device-map-cap-tree-label"
                        label="CAP radio slot"
                        value={addGroupTreeId}
                        onChange={(e) => setAddGroupTreeId(e.target.value)}
                      >
                        {addGroupCapTreeIds.map((id) => (
                          <MenuItem key={id} value={id}>{id}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )
                )}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAddToGroup}
                  disabled={
                    !addGroupId
                    || saving
                    || addGroupConfigLoading
                    || (addToGroupNeedsCapTree && !addGroupTreeId)
                  }
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <GroupAddIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {saving ? 'Adding…' : 'Add to group'}
                </Button>
              </Box>
            ) : (
              !isManageMode && (
                <Alert severity="info">
                  No groups exist yet. Create a group under WiFi Network → Configuration, or select devices
                  on the map and use the polygon selection dialog.
                </Alert>
              )
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceMapGroupDialog;
